import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Users, Dice5, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DrawReveal from "@/components/DrawReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Grand tirage au sort de fin de campagne (jeux instant gagnant).
 * - Avec onGameChange (admin) : configuration + tirage.
 * - Sans onGameChange (commerçant) : résumé lecture seule + tirage.
 * Le tirage est DÉFINITIF (un seul tirage possible).
 */
export default function GrandDrawPanel({ game, onGameChange }) {
  const { t } = useTranslation();
  const canConfigure = typeof onGameChange === "function";
  const enabled = !!game.grand_draw_enabled;
  const [stats, setStats] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [winners, setWinners] = useState([]);
  const [reveal, setReveal] = useState(null); // null | { phase, winners }

  useEffect(() => {
    if (!game?.id || !enabled) return;
    api
      .get(`/games/${game.id}/grand-draw`)
      .then((r) => {
        setStats(r.data);
        setWinners(r.data.winners || []);
      })
      .catch(() => {});
  }, [game?.id, enabled]);

  async function runDraw() {
    if (drawing) return;
    setDrawing(true);
    // Suspense: 5 s countdown overlay while the draw runs server-side.
    setReveal({ phase: "countdown", winners: [] });
    const suspense = new Promise((res) => setTimeout(res, 5000));
    try {
      const [{ data }] = await Promise.all([
        api.post(`/games/${game.id}/grand-draw`),
        suspense,
      ]);
      setWinners(data.winners || []);
      setStats((s) => ({ ...(s || {}), drawn_at: data.drawn_at }));
      setReveal({ phase: "winners", winners: data.winners || [] });
    } catch (e) {
      setReveal(null);
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
      setConfirming(false);
    }
  }

  if (!canConfigure && !enabled) return null;

  const drawnAt = stats?.drawn_at || null;

  return (
    <div className="qrise-card p-6" data-testid="grand-draw-panel">
      <DrawReveal
        open={!!reveal}
        phase={reveal?.phase}
        participants={stats?.total_participants || 0}
        winners={reveal?.winners || []}
        onClose={() => setReveal(null)}
      />
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="h-4 w-4" style={{ color: "#d4af37" }} />
        <div className="text-sm font-semibold">{t("grand_draw.title")}</div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug mb-4">
        {t("grand_draw.subtitle")}
      </p>

      {canConfigure ? (
        <>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2.5 cursor-pointer">
            <div>
              <div className="text-xs font-semibold">{t("grand_draw.enable")}</div>
              <div className="text-[11px] text-muted-foreground">
                {t("grand_draw.enable_hint")}
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => onGameChange({ grand_draw_enabled: v })}
              data-testid="grand-draw-enable-switch"
            />
          </label>

          {enabled && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">
                  {t("grand_draw.winners_count")}
                </Label>
                <Input
                  type="number"
                  min={1}
                  value={game.grand_draw_winners_count ?? 1}
                  onChange={(e) =>
                    onGameChange({
                      grand_draw_winners_count: Math.max(
                        1,
                        parseInt(e.target.value, 10) || 1
                      ),
                    })
                  }
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="grand-draw-winners-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">
                  {t("grand_draw.eligibility")}
                </Label>
                <Select
                  value={game.grand_draw_eligibility || "all"}
                  onValueChange={(v) => onGameChange({ grand_draw_eligibility: v })}
                >
                  <SelectTrigger
                    className="mt-1.5 rounded-xl h-10"
                    data-testid="grand-draw-eligibility-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("grand_draw.eligibility_all")}
                    </SelectItem>
                    <SelectItem value="losers">
                      {t("grand_draw.eligibility_losers")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-border/60 px-3 py-2.5 text-[11px] text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {t("grand_draw.merchant_locked")}{" "}
          {t("grand_draw.winners_count")} : {stats?.winners_count ?? game.grand_draw_winners_count ?? 1} ·{" "}
          {(stats?.eligibility ?? game.grand_draw_eligibility) === "losers"
            ? t("grand_draw.eligibility_losers")
            : t("grand_draw.eligibility_all")}
        </div>
      )}

      {enabled && (
        <div className="mt-4 rounded-xl border border-border p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[140px]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
                <Users className="h-3 w-3" />
                {t("grand_draw.participants")}
              </div>
              <div
                className="mt-0.5 text-2xl font-semibold tracking-tight"
                data-testid="grand-draw-participants"
              >
                {stats?.total_participants ?? "—"}
              </div>
            </div>

            {drawnAt ? (
              <div
                className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400"
                data-testid="grand-draw-done-note"
              >
                {t("grand_draw.drawn_at", {
                  date: new Date(drawnAt).toLocaleString(),
                })}
                <div className="font-normal text-muted-foreground">
                  {t("grand_draw.done_note")}
                </div>
              </div>
            ) : confirming ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirming(false)}
                  disabled={drawing}
                  className="h-10 rounded-xl text-xs"
                  data-testid="grand-draw-cancel-btn"
                >
                  {t("grand_draw.cancel")}
                </Button>
                <Button
                  type="button"
                  onClick={runDraw}
                  disabled={drawing}
                  className="h-10 rounded-xl text-xs font-bold tracking-[0.14em] uppercase"
                  style={{
                    background:
                      "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                    color: "#1a1410",
                  }}
                  data-testid="grand-draw-confirm-btn"
                >
                  {drawing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      {t("grand_draw.drawing")}
                    </>
                  ) : (
                    t("grand_draw.confirm_btn")
                  )}
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!stats?.total_participants}
                className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
                style={{
                  background:
                    "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                  color: "#1a1410",
                  boxShadow:
                    "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
                }}
                data-testid="grand-draw-run-btn"
              >
                <Dice5 className="h-4 w-4 mr-1.5" />
                {t("grand_draw.draw_btn")}
              </Button>
            )}
          </div>

          {!drawnAt && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              {stats?.total_participants
                ? t("grand_draw.definitive_note")
                : t("grand_draw.no_participants")}
            </div>
          )}

          {winners.length > 0 && (
            <div
              className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4"
              data-testid="grand-draw-winners-list"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
                <Trophy className="h-3.5 w-3.5" />
                {t("grand_draw.winners_title", { count: winners.length })}
              </div>
              <ul className="space-y-2">
                {winners.map((w, i) => (
                  <li
                    key={w.session_id}
                    className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                    data-testid={`grand-draw-winner-${i}`}
                  >
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        background:
                          "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                        color: "#1a1410",
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {w.first_name} {w.last_name}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {w.email}
                        {w.phone ? ` · ${w.phone}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
